/*
 * En error boundary MÅSTE vara en klass — React har ingen hook-motsvarighet till
 * componentDidCatch/getDerivedStateFromError. Projektets funktionella lint-regler
 * stängs därför av i just den här filen, på samma sätt som RouteErrorBoundary gör.
 */
/* eslint-disable functional/no-class, functional/no-this-expression, no-restricted-syntax */
import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import ErrorIcon from '@mui/icons-material/Error';
import ReplayIcon from '@mui/icons-material/Replay';
import { useTranslation } from 'react-i18next';

/**
 * Appens YTTERSTA felgräns. Mountas i index.tsx runt hela <App />.
 *
 * RouteErrorBoundary sitter långt inne i trädet (innanför CalendarScene,
 * RoutineScene, LocationScene, CharacterScene, StarScene, ChildOnboardingGate och
 * Layout) och fångar därför bara fel i själva rutt-vyerna. Ett fel i något av
 * lagren UTANFÖR den — providers, gater, temat, rollväljaren — tar ner hela
 * React-trädet och lämnar en VIT skärm. För ett strukturstöd är skillnaden mellan
 * "den här sidan strular" och "appen är borta" hela poängen: målgruppen kan inte
 * felsöka, och en vit skärm har ingen väg vidare alls.
 *
 * Fallbacken är medvetet BEROENDEFRI. Den får inte förutsätta något av det som kan
 * ha kraschat: ingen router (ErrorViews <Link> kräver en Router-context och skulle
 * kasta igen, vilket ger tillbaka den vita skärmen), ingen AppContext, inget
 * app-tema. MUI faller tillbaka på sitt standardtema utan ThemeProvider, och
 * useTranslation använder den globala i18next-instansen utan provider — saknas
 * översättningen visas nyckeln, vilket fortfarande är läsbar text.
 *
 * Vägen vidare är en HÅRD omstart till '/' i stället för att nollställa state:
 * gränsen fångar fel i trädets rot, och det state som fick trädet att krascha
 * sitter i de providers som en ren setState inte återställer.
 */

const AppErrorScreen = () => {
  const { t } = useTranslation();
  return (
    <Stack
      role="alert"
      spacing={2}
      sx={{
        minHeight: '100vh',
        justifyContent: 'center',
        alignItems: 'center',
        p: 3,
      }}
    >
      <ErrorIcon sx={{ fontSize: '5em', color: 'grey.700' }} />
      <Typography variant="h5" component="h1" textAlign="center">
        {t('Something went wrong!')}
      </Typography>
      <Typography variant="body1" textAlign="center">
        {t('An error occurred, please try again.')}
      </Typography>
      <Button
        size="large"
        variant="contained"
        startIcon={<ReplayIcon />}
        sx={{ minWidth: '15em' }}
        onClick={() => window.location.assign('/')}
      >
        {t('Try again')}
      </Button>
    </Stack>
  );
};

type Props = {
  children?: ReactNode;
};

type State = {
  failed: boolean;
};

class AppErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Konsolen är den enda platsen felet finns kvar efter att trädet bytts ut.
    console.error('Renderingsfel på toppnivå:', error, info.componentStack);
  }

  render() {
    if (this.state.failed) return <AppErrorScreen />;
    return <>{this.props.children}</>;
  }
}

export default AppErrorBoundary;
